import { useState, useEffect, useCallback } from "react";
import { useApi } from "../hooks/useApi";
import Sidebar from "../components/Sidebar";
import { Topbar, PageError, friendlyError } from "../components";

interface UserRecord {
  id:             string;
  email:          string;
  role:           "admin" | "operator" | "viewer";
  is_active:      boolean;
  invite_pending: boolean;
  last_login_at:  string | null;
  created_at:     string;
}

const ROLE_COLORS: Record<string, string> = {
  admin:    "var(--teal)",
  operator: "var(--blue)",
  viewer:   "var(--text3)",
};

function formatDate(ts: string | null): string {
  if (!ts) return "Never";
  const d = new Date(ts);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function UsersPage() {
  const { get, post, patch, del } = useApi();
  const [users, setUsers]         = useState<UserRecord[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [inviteOpen, setInviteOpen]   = useState(false);
  const [removeTarget, setRemoveTarget] = useState<UserRecord | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole]   = useState<"admin" | "operator" | "viewer">("viewer");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult]   = useState<{ url: string; email: string } | null>(null);
  const [inviteError, setInviteError]     = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    get<{ users: UserRecord[] }>("/api/users")
      .then(r => setUsers(r.users ?? []))
      .catch(err => setError(friendlyError(err)))
      .finally(() => setLoading(false));
  }, [get]);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteError("");
    try {
      const res = await post<{ user: UserRecord; invite_url: string }>("/api/users/invite", {
        email: inviteEmail,
        role:  inviteRole,
      });
      setInviteResult({ url: res.invite_url, email: res.user.email });
      load();
    } catch (err: any) {
      setInviteError(err.message ?? "Failed to send invite");
    } finally {
      setInviteLoading(false);
    }
  };

  const closeInviteModal = () => {
    setInviteOpen(false);
    setInviteEmail("");
    setInviteRole("viewer");
    setInviteResult(null);
    setInviteError("");
    setCopied(false);
  };

  const handleRoleChange = async (userId: string, role: string) => {
    await patch(`/api/users/${userId}`, { role });
    load();
  };

  const handleDeactivate = async (userId: string) => {
    if (!confirm("Deactivate this user?")) return;
    await del(`/api/users/${userId}`);
    load();
  };

  const handleReactivate = async (userId: string) => {
    await patch(`/api/users/${userId}`, { is_active: true });
    load();
  };

  const handleRemoveConfirm = async () => {
    if (!removeTarget) return;
    setRemoveLoading(true);
    try {
      await post(`/api/users/${removeTarget.id}/remove`, {});
      setRemoveTarget(null);
      load();
    } finally {
      setRemoveLoading(false);
    }
  };

  const copyInviteUrl = () => {
    if (inviteResult?.url) {
      navigator.clipboard.writeText(inviteResult.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const activeUsers   = users.filter(u => u.is_active);
  const inactiveUsers = users.filter(u => !u.is_active);

  return (
    <div className="shell">
      <Sidebar />
      <div className="main-column">
        <Topbar
          title="Users"
          subtitle={`${activeUsers.length} active user${activeUsers.length !== 1 ? "s" : ""}`}
          action={
            <button className="btn btn-primary" onClick={() => setInviteOpen(true)}>
              + Invite user
            </button>
          }
        />
        <main className="page">
          {error && <PageError message={error} onRetry={load} />}

          {!error && (loading ? (
            <div style={{ textAlign: "center", color: "var(--text3)", padding: "60px 0" }}>Loading…</div>
          ) : (
            <>
              <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--border)" }}>
                      {["Email", "Role", "Status", "Last login", "Joined", ""].map(h => (
                        <th key={h} style={{
                          textAlign: "left", padding: "10px 16px",
                          fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)",
                          fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.06em",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeUsers.map(u => (
                      <UserRow
                        key={u.id}
                        user={u}
                        onRoleChange={handleRoleChange}
                        onDeactivate={handleDeactivate}
                        onReactivate={handleReactivate}
                        onRemove={setRemoveTarget}
                      />
                    ))}
                    {inactiveUsers.map(u => (
                      <UserRow
                        key={u.id}
                        user={u}
                        onRoleChange={handleRoleChange}
                        onDeactivate={handleDeactivate}
                        onReactivate={handleReactivate}
                        onRemove={setRemoveTarget}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ))}
        </main>
      </div>

      {/* Remove confirmation modal */}
      {removeTarget && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.55)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 100,
        }}>
          <div style={{
            width: 400, background: "var(--surface)",
            border: "1px solid var(--border2)", borderRadius: "var(--r2)",
            padding: 28, boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          }}>
            <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Remove user?</h2>
            <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 8 }}>
              This will permanently remove <strong>{removeTarget.email}</strong> from your account.
            </p>
            <p style={{ fontSize: 13, color: "var(--text3)", marginBottom: 24 }}>
              Their audit history will be retained for 1 year, then automatically purged. This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-ghost" onClick={() => setRemoveTarget(null)} disabled={removeLoading}>
                Cancel
              </button>
              <button
                className="btn"
                onClick={handleRemoveConfirm}
                disabled={removeLoading}
                style={{ background: "#DC2626", color: "#fff", border: "none" }}
              >
                {removeLoading ? "Removing…" : "Remove permanently"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite modal */}
      {inviteOpen && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.55)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 100,
        }}
          onClick={e => { if (e.target === e.currentTarget) closeInviteModal(); }}
        >
          <div style={{
            width: 420, background: "var(--surface)",
            border: "1px solid var(--border2)", borderRadius: "var(--r2)",
            padding: 28, boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          }}>
            {inviteResult ? (
              <>
                <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Invite sent</h2>
                <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 4 }}>
                  An invitation email has been sent to <strong>{inviteResult.email}</strong>.
                </p>
                <p style={{ fontSize: 13, color: "var(--text3)", marginBottom: 16 }}>
                  If they don't receive it, you can share the link below directly. It expires in 7 days.
                </p>
                <div style={{
                  display: "flex", gap: 8, alignItems: "center",
                  padding: "8px 12px", background: "var(--surface2)",
                  border: "1px solid var(--border)", borderRadius: "var(--r)",
                  fontFamily: "var(--mono)", fontSize: 11, color: "var(--text2)",
                  wordBreak: "break-all",
                }}>
                  <span style={{ flex: 1 }}>{inviteResult.url}</span>
                  <button className="btn btn-ghost btn-sm" onClick={copyInviteUrl} style={{ flexShrink: 0 }}>
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                  <button className="btn btn-primary" onClick={closeInviteModal}>Done</button>
                </div>
              </>
            ) : (
              <>
                <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 20 }}>Invite user</h2>
                {inviteError && (
                  <div style={{
                    background: "var(--red-bg)", color: "#791F1F", fontSize: 13,
                    padding: "10px 14px", borderRadius: "var(--r)", marginBottom: 16,
                  }}>{inviteError}</div>
                )}
                <form onSubmit={handleInvite} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Email address
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={e => setInviteEmail(e.target.value)}
                      required
                      placeholder="colleague@example.com"
                      className="form-input"
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Role
                    </label>
                    <select
                      value={inviteRole}
                      onChange={e => setInviteRole(e.target.value as any)}
                      className="form-input"
                      style={{ width: "100%" }}
                    >
                      <option value="viewer">Viewer — read-only access</option>
                      <option value="operator">Operator — can trigger backups</option>
                      <option value="admin">Admin — full access</option>
                    </select>
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
                    <button type="button" className="btn btn-ghost" onClick={closeInviteModal}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={inviteLoading}>
                      {inviteLoading ? "Inviting…" : "Send invite"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function UserRow({
  user,
  onRoleChange,
  onDeactivate,
  onReactivate,
  onRemove,
}: {
  user: UserRecord;
  onRoleChange: (id: string, role: string) => void;
  onDeactivate: (id: string) => void;
  onReactivate: (id: string) => void;
  onRemove: (user: UserRecord) => void;
}) {
  return (
    <tr style={{
      borderBottom: "1px solid var(--border)",
      opacity: user.is_active ? 1 : 0.5,
    }}>
      <td style={{ padding: "10px 16px", fontWeight: 500 }}>
        {user.email}
        {user.invite_pending && (
          <span style={{
            marginLeft: 8, fontSize: 10, fontFamily: "var(--mono)",
            color: "var(--text3)", background: "var(--surface2)",
            border: "1px solid var(--border)", borderRadius: "var(--r)",
            padding: "1px 5px",
          }}>invite pending</span>
        )}
      </td>
      <td style={{ padding: "10px 16px" }}>
        {user.is_active ? (
          <select
            value={user.role}
            onChange={e => onRoleChange(user.id, e.target.value)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: 13, fontFamily: "var(--sans)",
              color: ROLE_COLORS[user.role] ?? "var(--text)",
              fontWeight: 500, padding: 0,
            }}
          >
            <option value="viewer">Viewer</option>
            <option value="operator">Operator</option>
            <option value="admin">Admin</option>
          </select>
        ) : (
          <span style={{ color: ROLE_COLORS[user.role] ?? "var(--text3)", fontWeight: 500 }}>
            {user.role}
          </span>
        )}
      </td>
      <td style={{ padding: "10px 16px" }}>
        {user.is_active ? (
          <span style={{ color: "var(--teal)", fontSize: 12 }}>● Active</span>
        ) : (
          <span style={{ color: "var(--text3)", fontSize: 12 }}>● Inactive</span>
        )}
      </td>
      <td style={{ padding: "10px 16px", color: "var(--text3)", fontSize: 12, fontFamily: "var(--mono)" }}>
        {formatDate(user.last_login_at)}
      </td>
      <td style={{ padding: "10px 16px", color: "var(--text3)", fontSize: 12, fontFamily: "var(--mono)" }}>
        {formatDate(user.created_at)}
      </td>
      <td style={{ padding: "10px 16px", textAlign: "right" }}>
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          {user.is_active ? (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => onDeactivate(user.id)}
              style={{ color: "var(--text3)" }}
            >
              Deactivate
            </button>
          ) : (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => onReactivate(user.id)}
            >
              Reactivate
            </button>
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => onRemove(user)}
            style={{ color: "#DC2626" }}
          >
            Remove
          </button>
        </div>
      </td>
    </tr>
  );
}
